import React, { useCallback } from 'react';
import { Button } from 'antd';
import { AsyncThunk } from '@reduxjs/toolkit';

import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { downloadFile, downloadFileByObjectURL } from 'utils/fileUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  getCustomersDetailExcel,
  getCustomerCertificateOfInsuranceCreatedFile,
  getServiceAgreementFileInfoForCustomer,
} from 'redux/reducers/clientSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  DownloadAgreementTypeEnum,
  FileLoadStatus,
} from '../DownloadClientAgreement/DownloadClientAgreement';
import { getRecruiterPriceAgreementInfoFile } from 'redux/reducers/recruiterSlice';

import styles from '../ClientDetailsPage.module.less';

type Props = {
  clientId?: string;
  hasCertificateOfInsurance: boolean;
  listOfAgreementFiles?: FileLoadStatus[];
  downloadAgreementType?: DownloadAgreementTypeEnum;
  id?: string;
};

export default function DownloadClient(props: Props) {
  const {
    clientId,
    hasCertificateOfInsurance,
    listOfAgreementFiles = [],
    downloadAgreementType,
    id,
  } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();

  const loadAgreemtnFileInfo = useCallback(
    async (fileKey: string, fileName: string) => {
      let requestData: any;
      let correctAction: AsyncThunk<ResourceDownloadResponse, any, {}>;

      switch (downloadAgreementType) {
        case DownloadAgreementTypeEnum.Customer:
          correctAction = getServiceAgreementFileInfoForCustomer;
          requestData = {
            customerId: id as string,
            isAdminRole,
            fileKey,
            fileName,
          };
          break;
        case DownloadAgreementTypeEnum.Member:
        default:
          correctAction = getRecruiterPriceAgreementInfoFile;
          requestData = {
            userId: id as string,
            fileKey,
            fileName,
            isAdmin: isAdminRole,
          };
      }

      const result = await dispatch(correctAction(requestData));

      if (correctAction.fulfilled.match(result)) {
        return result.payload;
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);

        return Promise.reject();
      }
    },
    [id, dispatch, isAdminRole, downloadAgreementType],
  );

  const loadAgreementFiles = async (filesInfo: FileLoadStatus[]) => {
    const promises = filesInfo.map((file) =>
      loadAgreemtnFileInfo(file.fileKey, file.fileName),
    );

    const responses = await Promise.allSettled(promises);

    const payloads = responses
      .filter((result) => result.status === 'fulfilled')
      .map(
        (result) =>
          (result as PromiseFulfilledResult<ResourceDownloadResponse>).value,
      );

    Promise.allSettled(payloads.map(downloadClientFile));
  };

  const handleDownloadClient = async () => {
    const result = await dispatch(
      getCustomersDetailExcel({ customerId: clientId as string, isAdminRole }),
    );

    if (getCustomersDetailExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);

      await downloadClientAttachedFiles();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadCustomerCertificateOfInsuranceCreatedFile = async () => {
    const result = await dispatch(
      getCustomerCertificateOfInsuranceCreatedFile({
        customerId: clientId as string,
        isAdminRole,
      }),
    );

    if (getCustomerCertificateOfInsuranceCreatedFile.fulfilled.match(result)) {
      return result.payload;
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      return Promise.reject();
    }
  };

  const downloadClientFile = async (
    downloadResponse: ResourceDownloadResponse,
  ) => {
    try {
      const { downloadUrl, fileName } = downloadResponse;

      await downloadFile({ downloadUrl, fileName });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  const downloadClientAttachedFiles = async () => {
    let downloadRequests: Promise<ResourceDownloadResponse>[] = [];

    if (listOfAgreementFiles?.length) {
      await loadAgreementFiles(listOfAgreementFiles);
    }

    hasCertificateOfInsurance &&
      downloadRequests.push(loadCustomerCertificateOfInsuranceCreatedFile());

    const responses = await Promise.allSettled(downloadRequests);
    const payloads: ResourceDownloadResponse[] = responses
      .filter((response) => response.status === 'fulfilled')
      .map((response) => response.value);

    Promise.allSettled(payloads.map(downloadClientFile));
  };

  return (
    <Button
      className={styles.downloadBtn}
      onClick={handleDownloadClient}
      icon={<span className="icon icon-xls-file" />}
    />
  );
}
